import React from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  PlayCircleOutline,
} from '@mui/icons-material';
import { Menu } from '../Menu';
import { UserProfile } from '../auth/UserProfile';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { DEMO_LOGIN_ENABLED } from '@/config/demoLogin';
import { usePersonLabels } from '@/hooks/usePersonLabels';

interface DrawerContentProps {
  collapsed: boolean;
  onCollapseToggle: () => void;
}

export const DrawerContent: React.FC<DrawerContentProps> = ({ collapsed, onCollapseToggle }) => {
  const { isAuthenticated, user, isServiceMember, hasRole, isFrontWindowOnly, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { label: personLabel } = usePersonLabels('front_window');

  // Keep the authenticated session so ServiceMemberLoginPage's auth gate passes (#323).
  const handleStartDemo = () => {
    navigate('/service-member-login');
  };

  // Mirrors the isAllowedRole guard in ServiceMemberLoginPage: admin/user/frontwindow/comptroller.
  // Viewer, finance, and hr do not have an operational impersonation context (#512).
  const canStartImpersonation =
    isAuthenticated &&
    !isServiceMember &&
    !hasRole('viewer') &&
    !hasRole('finance') &&
    !hasRole('hr') &&
    (hasRole('user') || isFrontWindowOnly || hasRole('comptroller') || isAdmin);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {DEMO_LOGIN_ENABLED && canStartImpersonation && (
        <Box sx={{ px: 1, py: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            color="warning"
            startIcon={<PlayCircleOutline fontSize="small" />}
            onClick={() => handleStartDemo()}
            sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
          >
            {collapsed ? '' : `Start ${personLabel} Impersonation`}
          </Button>
        </Box>
      )}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Menu collapsed={collapsed} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: collapsed ? 0.5 : 1,
          px: collapsed ? 0.5 : 2,
          py: 1,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
        }}
      >
        {isAuthenticated && user && (
          <Box sx={{ minWidth: 0, flex: collapsed ? undefined : 1 }}>
            <UserProfile variant="full" collapsed={collapsed} />
          </Box>
        )}
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <span>
              <IconButton
                color="default"
                size="small"
                aria-label={collapsed ? 'expand sidebar' : 'collapse sidebar'}
                onClick={onCollapseToggle}
              >
                {collapsed ? <KeyboardDoubleArrowRight /> : <KeyboardDoubleArrowLeft />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
